export type ReportType = 'group_profile' | 'group_profile_plus' | 'team_member_report';

/**
 * A saved, re-runnable report definition.
 *
 * `participantIds` REFERENCES participants who live in the invitations store — it never copies
 * them. A report is a definition over people, not an owner of them, so a name or score edited on
 * the invitation shows through with no sync step.
 */
export interface Report {
  id: string;
  title: string;
  /** The organization that owns the report. Independent of where its participants came from. */
  ownerOrgId: string;
  /** Denormalised for the listing, exactly as Invitation.rosterName is. */
  ownerOrgName: string;
  type: ReportType;
  /**
   * Organizations the participants were drawn from — the picker's chip-bar, remembered so Edit
   * can rehydrate it. Without this, reopening a report would show the chosen people with no idea
   * which org chips produced them.
   */
  sourceOrgIds: string[];
  /** Participant.id values, in the order they were added. */
  participantIds: string[];
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/** Payload from the wizard. The service fills in id, ownerOrgName, createdBy and the stamps. */
export interface ReportDraft {
  title: string;
  ownerOrgId: string;
  type: ReportType;
  sourceOrgIds: string[];
  participantIds: string[];
}

export const REPORT_TYPE_LABELS: Record<ReportType, string> = {
  group_profile: 'Group Profile',
  group_profile_plus: 'Group Profile Plus',
  team_member_report: 'Team Member Profile',
};

/**
 * A monochrome FontAwesome icon per type — the Type pill is neutral grey, so the icon (not colour)
 * carries the visual difference: group vs group-plus vs a single member.
 */
export const REPORT_TYPE_ICON: Record<ReportType, string> = {
  group_profile: 'fa-users',
  group_profile_plus: 'fa-star',
  team_member_report: 'fa-user',
};

/**
 * Which inventory balance each type draws on — `null` is free.
 *
 * INFORMATIONAL ONLY. Nothing debits: generating a report does not spend a key, and there is no
 * ledger entry. Whether these are really the same pool as invitations' scoring keys is an open
 * product question. Do not turn this display into a transaction without designing it.
 */
export const REPORT_TYPE_KEY: Record<ReportType, 'group' | 'team' | null> = {
  group_profile: null,
  group_profile_plus: 'group',
  team_member_report: 'team',
};

/**
 * The roster enforces a 20-member ceiling on the Team Member Report. Known issue, deliberately
 * NOT enforced here — the product decision is to warn and allow while the cap is reconsidered.
 */
export const TEAM_MEMBER_REPORT_CAP = 20;
